/* eslint-disable prettier/prettier */
import React, {
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button, Box } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import Table from "../shared/Table";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import { useAppContext } from "../../api/AppContext";
import { useModals } from "../../hooks/useModals";
import { isCyberTipsSection, getCyberTipsEntityLabel } from "./cyberTipsSections";
import {
  COMPLETION_STATUS_STYLES,
  getEditableLanguagesForPromptSection,
  getLanguageChipStyles,
  getLocalizedField,
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  hasPromptContentInLanguage,
  isNonEmptyString,
  isPromptLanguageCompleteForSave,
  isTagNameCompleteForSave,
  LANGUAGE_CHIP_LABELS,
} from "../shared/languageConfig";

const MassiveImportButton = ({ currentTab }) => {
  const modals = useModals();
  const { updatePrompt } = useAppContext();
  const location = useLocation();
  const basePath = useMemo(
    () => location.pathname.split("/")[1],
    [location.pathname]
  );

  const handleSinglePromptCreation = useCallback(
    async (data) => {
      const tmpData = { ...data };
      if ("originalRecord" in tmpData) {
        delete tmpData.originalRecord;
      }
      await updatePrompt(
        { isNew: true, ...tmpData, skipLocalUpdate: true },
        basePath
      );
    },
    [updatePrompt, basePath]
  );

  if (currentTab === "prompt" && basePath === "copilot") {
    return (
      <Button
        key="massive-import"
        color="primary"
        endIcon={<AddIcon />}
        variant="contained"
        onClick={() => {
          modals.openModal(modals.modalTypes.SheetImport, {
            onConfirm: handleSinglePromptCreation,
          });
        }}
      >
        Import from Excel
      </Button>
    );
  } else return <></>;
};


const CopilotTable = () => {
  const {
    prompts,
    fetchPrompts,
    tools,
    fetchTools,
    tags,
    fetchTags,
    deletePrompt,
    deleteTool,
    deleteTag,
    seeds,
    aiSkillingPrompts,
    cyberTipsCdpPrompts,
    configurations,
    fetchConfigurations,
    user,
  } = useAppContext();

  const location = useLocation();
  const history = useHistory();
  const tableRef = useRef();
  const modals = useModals();

  const basePath = useMemo(
    () => location.pathname.split("/")[1],
    [location.pathname]
  );
  const currentTab = useMemo(
    () => location.pathname.split("/")[2],
    [location.pathname]
  );
  const supportsTools = useMemo(
    () => !isCyberTipsSection(basePath),
    [basePath]
  );
  const isUserPromptsTab = useMemo(
    () => basePath === "copilot" && currentTab === "user-prompts",
    [basePath, currentTab]
  );
  const isPromptLikeTab = useMemo(
    () => currentTab === "prompt" || currentTab === "user-prompts",
    [currentTab]
  );
  const companyLanguages = useMemo(
    () => getValidLanguagesFromUser(user),
    [user]
  );
  const tableLanguages = useMemo(
    () => getEditableLanguagesForPromptSection(basePath, companyLanguages),
    [basePath, companyLanguages]
  );
  const [activeTableLanguage, setActiveTableLanguage] = useState(() =>
    getPreferredLanguageFromUser(user, tableLanguages)
  );

  useEffect(() => {
    const preferred = getPreferredLanguageFromUser(user, tableLanguages);
    setActiveTableLanguage((current) =>
      tableLanguages.includes(current) ? current : preferred
    );
  }, [tableLanguages, user]);

  const getTitle = useCallback(
    ({ targetKeyword } = {}) => {
      const tab = targetKeyword || currentTab;
      switch (tab) {
        case "prompt":
          switch (basePath) {
            case "allitude":
              return "Seeds";
            default:
              if (isCyberTipsSection(basePath)) {
                return getCyberTipsEntityLabel({ plural: true });
              }
              return "Prompts";
          }
        case "tools":
          switch (basePath) {
            case "allitude":
              return "Topics";
            default:
              return "Tools";
          }
        case "tags":
          return "Tags";
        case "user-prompts":
          return "User Prompts";
        default:
          return "";
      }
    },
    [currentTab, basePath]
  );

  const getMultilingualValue = useCallback(
    (rowData, field, language) => getLocalizedField(rowData[field], language),
    []
  );

  const getPathSpecificColumns = useCallback(
    (tab, activeLanguage) => {
      const customFilterAndSearch = (term, rowData, columnDef) => {
        let searchValue;

        if (["title", "description", "prompttext", "tagname"].includes(columnDef.field)) {
          searchValue = getLocalizedField(
            rowData[columnDef.field],
            activeLanguage
          );
        } else if (columnDef.field === "tags") {
          searchValue = rowData.tags
            ?.map((tag) => getLocalizedField(tag, activeLanguage))
            .filter(isNonEmptyString)
            .join(", ");
        } else {
          searchValue = rowData[columnDef.field];
        }

        if (searchValue == null) {
          return false;
        }

        return String(searchValue).toLowerCase().includes(term.toLowerCase());
      };

      switch (tab) {
        case "prompt":
        case "user-prompts":
          return [
            {
              customFilterAndSearch,
              field: "title",
              render: (rowData) =>
                getMultilingualValue(rowData, "title", activeLanguage),
              title: "Title",
            },
            {
              customFilterAndSearch,
              field: "description",
              render: (rowData) =>
                getMultilingualValue(rowData, "description", activeLanguage),
              title: "Description",
            },
            ...(isUserPromptsTab
              ? [
                  {
                    field: "did",
                    title: "Did",
                  },
                  {
                    field: "cid",
                    render: (rowData) => {
                      const config = configurations.find(
                        (item) => item.cid === rowData.cid
                      );
                      return config?.name || rowData.cid || "";
                    },
                    title: "Cid",
                  },
                  {
                    field: "processed",
                    render: (rowData) => (rowData.processed ? "Yes" : "No"),
                    title: "Processed",
                  },
                ]
              : []),
            {
              customFilterAndSearch,
              field: "prompttext",
              render: (rowData) =>
                getMultilingualValue(rowData, "prompttext", activeLanguage),
              title: getTitle({ targetKeyword: "prompt" }).slice(0, -1),
            },
            ...(supportsTools
              ? [
                  {
                    field: "toolname",
                    title: `${getTitle({ targetKeyword: "tools" }).slice(0, -1)}`,
                  },
                ]
              : []),
            {
              customFilterAndSearch,
              field: "tags",
              render: (rowData) => {
                const localizedTags = rowData?.tags
                  ?.map((tag) => getLocalizedField(tag, activeLanguage))
                  .filter(isNonEmptyString);

                return localizedTags?.length
                  ? localizedTags.join(", ")
                  : "No Tags";
              },
              title: "Tags",
            },
            ...(isCyberTipsSection(basePath)
              ? []
              : isUserPromptsTab
              ? []
              : [{ field: "isfromtypeform", title: "isFromTypeform" }]),
          ];
      case "tools":
        return [
          {
            field: "toolname",
            title: `${getTitle({ targetKeyword: tab }).slice(0, -1)} Name`,
          },
          { field: "colortop", title: "Color Top" },
          { field: "colorbottom", title: "Color Bottom" },
        ];
      case "tags":
        return [
          {
            customFilterAndSearch,
            field: "tagname",
            render: (rowData) =>
              getMultilingualValue(rowData, "tagname", activeLanguage),
            title: "Name",
          },
          { field: "tagcolor", title: "Color" },
        ];
      default:
        return [];
    }
  },
    [
      basePath,
      configurations,
      getMultilingualValue,
      getTitle,
      isUserPromptsTab,
      supportsTools,
    ]
  );

  const columns = useMemo(
    () => getPathSpecificColumns(currentTab, activeTableLanguage),
    [activeTableLanguage, currentTab, getPathSpecificColumns]
  );

  const fetchData = useCallback(async () => {
    switch (currentTab) {
      case "prompt":
      case "user-prompts":
        await fetchPrompts(basePath);
        break;
      case "tools":
        if (supportsTools) {
          await fetchTools(basePath);
        }
        break;
      case "tags":
        await fetchTags(basePath);
        break;
      default:
        break;
    }
  }, [basePath, currentTab, fetchPrompts, fetchTools, fetchTags, supportsTools]);

  useEffect(() => {
    fetchData();
  }, [fetchData, currentTab]);

  useEffect(() => {
    if (isUserPromptsTab && configurations.length === 0) {
      fetchConfigurations();
    }
  }, [configurations.length, fetchConfigurations, isUserPromptsTab]);

  const data = useMemo(() => {
    const paths = {
      aiskilling: aiSkillingPrompts,
      allitude: seeds,
      "cyber-tips-cdp": cyberTipsCdpPrompts,
    };
    switch (currentTab) {
      case "prompt": {
        return (paths[basePath] || prompts).filter((item) => {
          if (isUserPromptsTab && !item.sharetoeveryone) {
            return false;
          }

          return hasPromptContentInLanguage(item, activeTableLanguage);
        });
      }
      case "user-prompts":
        return (paths[basePath] || prompts).filter(
          (item) =>
            item.sharetoeveryone &&
            hasPromptContentInLanguage(item, activeTableLanguage)
        );
      case "tools":
        return supportsTools ? tools : [];
      case "tags":
        return tags.filter((tag) =>
          isTagNameCompleteForSave(tag.tagname, activeTableLanguage)
        );
      default:
        return [];
    }
  }, [
    activeTableLanguage,
    aiSkillingPrompts,
    cyberTipsCdpPrompts,
    seeds,
    currentTab,
    basePath,
    prompts,
    tools,
    tags,
    supportsTools,
    isUserPromptsTab,
  ]);

  const onRowDelete = useCallback(
    async (values) => {
      const { id } = values;
      switch (currentTab) {
        case "prompt":
          await deletePrompt({ id }, basePath);
          break;
        case "tools":
          if (supportsTools) {
            modals.openConfirmation({
              closeCTATitle: "Go back",
              confirmCTATitle: "Delete",
              onConfirm: async () => {
                await deleteTool({ id }, basePath);
              },
              text: "All the prompts associated with this tool will be deleted automatically.",
            });
          }
          break;
        case "tags":
          modals.openConfirmation({
            closeCTATitle: "Go back",
            confirmCTATitle: "Delete",
            onConfirm: async () => {
              await deleteTag({ id }, basePath);
            },
            text: "All the prompts which have only this tag will be deleted automatically and it will be removed from the prompts that have multiple tags.",
          });
          break;
        default:
          break;
      }
      fetchData();
    },
    [
      currentTab,
      fetchData,
      deletePrompt,
      basePath,
      modals,
      deleteTool,
      deleteTag,
      supportsTools,
    ]
  );

  const editable = useMemo(
    () => (isUserPromptsTab ? undefined : { onRowDelete }),
    [isUserPromptsTab, onRowDelete]
  );

  const languageChips = useMemo(() => {
    if (!isPromptLikeTab && currentTab !== "tags") {
      return null;
    }

    return (
      <Box display="flex" flexWrap="wrap" pt={0.5}>
        {tableLanguages.map((language) => (
          <Button
            key={language}
            size="small"
            style={{
              ...getLanguageChipStyles(activeTableLanguage === language),
              borderRadius: 20,
              boxShadow: "none",
              marginBottom: 8,
              marginRight: 8,
              minWidth: 90,
            }}
            variant="contained"
            onClick={() => setActiveTableLanguage(language)}
          >
            {LANGUAGE_CHIP_LABELS[language] || language.toUpperCase()}
          </Button>
        ))}
      </Box>
    );
  }, [activeTableLanguage, currentTab, isPromptLikeTab, tableLanguages]);

  const tableRowActions = useMemo(
    () => [
      {
        icon: (props) => <EditIcon {...props} color="action" />,
        onClick: (event, rowData) => {
          history.push(`/${basePath}/${currentTab}/${rowData.id}`, {
            isNew: false,
            rowData,
          });
        },
        tooltip: "Edit",
      },
    ],
    [basePath, currentTab, history]
  );

  const options = useMemo(
    () => ({
      rowStyle: (rowData) => {
        if (!isPromptLikeTab) {
          return {};
        }

        const isPartial =
          hasPromptContentInLanguage(rowData, activeTableLanguage) &&
          !isPromptLanguageCompleteForSave(rowData, activeTableLanguage);

        if (!isPartial) {
          return {};
        }

        return {
          backgroundColor: COMPLETION_STATUS_STYLES.partial.backgroundColor,
        };
      },
    }),
    [activeTableLanguage, isPromptLikeTab]
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Table
        actions={tableRowActions}
        columns={columns}
        data={data}
        editable={editable}
        languageChips={languageChips}
        options={options}
        refetch={fetchData}
        tableActions={
          isUserPromptsTab
            ? []
            : [
                <Button
                  key="add"
                  color="primary"
                  endIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => {
                    history.push(`/${basePath}/${currentTab}/new`, {
                      isNew: true,
                    });
                  }}
                >
                  Create new {getTitle().slice(0, -1)}
                </Button>,
                <MassiveImportButton
                  key="massive-import"
                  currentTab={currentTab}
                />,
              ]
        }
        tableRef={tableRef}
        title={getTitle()}
      />
    </Box>
  );
};

export default CopilotTable;
